import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Switch,
  Paper,
  Avatar,
  IconButton,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import apiService from "../../../service/Api";

interface MemoryItem {
  id: number;
  name: string;
  message: string;
  timestamp: string;
  approved: boolean;
}

interface MemoryCardProps {
  reduxTheme: any;
  name: string;
  time: string;
  description: string;
}

const MemoryCard: React.FC<MemoryCardProps> = ({
  reduxTheme,
  name,
  time,
  description,
}) => (
  <Paper
    sx={{
      p: 1,
      height: 120,
      mt: 2,
      position: "relative",
      width: 590,
      borderRadius: 2,
    }}
  >
    <IconButton
      size="small"
      aria-label="delete"
      sx={{
        position: "absolute",
        top: -8,
        right: -8,
        width: 20,
        height: 20,
        borderRadius: "50%",
        bgcolor: "background.paper",
        color: "text.secondary",
        boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
        "&:hover": { bgcolor: "grey.100" },
      }}
    >
      <DeleteOutlineOutlinedIcon
        fontSize="small"
        sx={{ color: reduxTheme.primary_colors?.[400] }}
      />
    </IconButton>

    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          width: 300,
          height: 50,
          boxShadow: 0,
          p: "2px 4px",
          borderRadius: 2,
          gap: 1,
        }}
      >
        <Avatar
          sx={{
            bgcolor: reduxTheme.primary_colors?.[400],
            width: 40,
            height: 40,
          }}
        >
          {name
            .split(" ")
            .map((n) => n[0] || "")
            .join("")
            .toUpperCase()}
        </Avatar>
        <Box display="flex" flexDirection="column" justifyContent="center">
          <Typography
            variant="h6"
            sx={{ color: "text.primary", fontSize: "0.8rem" }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", opacity: 0.7, fontSize: "0.7rem" }}
          >
            {time}
          </Typography>
        </Box>
      </Paper>
      <Switch size="small" />
    </Box>

    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
      {description}
    </Typography>
  </Paper>
);

// Typed API response
interface ApiMessage {
  id: number;
  sender_full_name: string | null;
  content: string;
  created_at: string;
  is_approved: boolean;
}

interface ApiMessageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiMessage[];
}

const MemoriesPanel = ({
  reduxTheme,
  deceasedId,
  bookingId,
}: {
  reduxTheme: any;
  deceasedId: number | undefined;
  bookingId: number | undefined;
}) => {
  const [memories, setMemories] = useState<MemoryItem[]>([]);

  const fetchMemories = async () => {
    try {
      const response = await apiService.get<ApiMessageResponse>(
        `api/chats/memorial-message/?booking_id=${bookingId}&is_approved=true`
      );

      const fetched: MemoryItem[] = (response.data.results || []).map(
        (msg) => ({
          id: msg.id,
          name: msg.sender_full_name || "Anonymous",
          message: msg.content,
          timestamp: new Date(msg.created_at).toLocaleString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
          approved: msg.is_approved,
        })
      );

      const uniqueMemories = Array.from(
        new Map(fetched.map((m) => [m.id, m])).values()
      );

      setMemories(uniqueMemories);
    } catch (error) {
      console.error("Failed to fetch memories:", error);
      setMemories([]);
    }
  };

  // Only changed this: fetch based on bookingId
  useEffect(() => {
    if (bookingId) fetchMemories();
  }, [bookingId]);

  return (
    <Card
      sx={{
        height: 485,
        backgroundColor: reduxTheme.primary_colors?.[800],
        borderRadius: 3,
        pt: 8,
        width: 660,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, height: "100%", overflow: "hidden" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Choose memories to display
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Enable/Disable
            </Typography>
            <Switch size="small" defaultChecked />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={1.5} overflow="hidden">
          {memories.map((m) => (
            <MemoryCard
              key={m.id}
              reduxTheme={reduxTheme}
              name={m.name}
              time={m.timestamp}
              description={m.message}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MemoriesPanel;
